import { useState, useEffect } from 'react';
import { DataTable } from '../../components/shared/DataTable';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { STATUS_CONFIG, CASE_STATUSES } from '../../data/mockData';
import { fetchUsers, fetchCases } from '../../api';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Plus,
  Users,
  FolderKanban,
  UserPlus,
  IndianRupee,
  TrendingUp,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
} from 'lucide-react';
import { format } from 'date-fns';

export default function VendorModule() {
  const [activeTab, setActiveTab] = useState('team');
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [selectedCaseIds, setSelectedCaseIds] = useState([]);
  const [verificationData, setVerificationData] = useState({
    status: '',
    remarks: ''
  });

  const [users, setUsers] = useState([]);
  const [cases, setCases] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        const [uRes, cRes] = await Promise.all([
          fetchUsers().catch(() => []),
          fetchCases().catch(() => [])
        ]);
        if (Array.isArray(uRes)) setUsers(uRes);
        if (Array.isArray(cRes)) setCases(cRes);
      } catch (err) {
        console.error("VendorModule data load error:", err);
      }
    }
    load();
  }, []);

  const handleBulkAction = (action, selectedIds) => {
    if (action === 'manual_verification') {
      setSelectedCaseIds(selectedIds);
      setShowVerificationModal(true);
    }
  };
  
  const vendorUsers = users.filter(u => u.role === 'vendor');
  const fieldExecutives = users.filter(u => u.role === 'field_executive');
  const vendorCases = cases.filter(c => c.assignedTo && ['vendor', 'field_executive'].includes(c.assignedTo.role));

  const feColumns = [
    {
      key: 'name',
      header: 'Field Executive',
      render: (row) => (
        <div className="flex items-center gap-3">
          <Avatar className="w-9 h-9">
            <AvatarImage src={row.avatar} />
            <AvatarFallback>{row.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-slate-900">{row.name}</p>
            <p className="text-xs text-slate-500">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (row) => <span className="text-sm text-slate-500">{row.phone}</span>,
    },
    {
      key: 'activeCases',
      header: 'Active Cases',
      render: () => (
        <span className="text-sm font-medium text-blue-600">
          {Math.floor(Math.random() * 10) + 1}
        </span>
      ),
    },
    {
      key: 'completedToday',
      header: 'Completed Today',
      render: () => (
        <span className="text-sm font-medium text-emerald-600">
          {Math.floor(Math.random() * 5)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <StatusBadge
          status={row.status === 'active' ? 'success' : 'neutral'}
          label={row.status === 'active' ? 'Active' : 'Offline'}
        />
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50">
            Assign Cases
          </Button>
          <Button variant="ghost" size="sm" className="text-xs text-slate-500 hover:text-slate-900">
            View Profile
          </Button>
        </div>
      ),
    },
  ];

  const caseColumns = [
    {
      key: 'refNumber',
      header: 'Case ID',
      render: (row) => (
        <span className="font-medium text-blue-600">{row.refNumber}</span>
      ),
    },
    {
      key: 'applicantName',
      header: 'Applicant',
      render: (row) => <span className="text-sm text-slate-900">{row.applicantName}</span>,
    },
    {
      key: 'product',
      header: 'Product',
      render: (row) => <span className="text-sm text-slate-500">{row.product.name}</span>,
    },
    {
      key: 'assignedTo',
      header: 'Assigned FE',
      render: (row) => row.assignedTo ? (
        <div className="flex items-center gap-2">
          <Avatar className="w-6 h-6">
            <AvatarImage src={row.assignedTo.avatar} />
            <AvatarFallback className="text-xs">{row.assignedTo.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <span className="text-sm text-slate-700">{row.assignedTo.name}</span>
        </div>
      ) : (
        <Button variant="ghost" size="sm" className="text-xs text-blue-600">
          Assign
        </Button>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <StatusBadge
          status={STATUS_CONFIG[row.status]?.color || 'neutral'}
          label={STATUS_CONFIG[row.status]?.label || row.status}
        />
      ),
    },
    {
      key: 'dueDate',
      header: 'Due Date',
      render: (row) => (
        <span className="text-sm text-slate-500">
          {format(new Date(row.dueDate), 'MMM dd')}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="vendor-module-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Vendor Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your team and assigned cases</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="add-fe-btn">
          <UserPlus className="w-4 h-4" />
          Add Field Executive
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="crm-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-805">{fieldExecutives.length}</p>
              <p className="text-xs text-slate-500">Team Members</p>
            </div>
          </div>
        </div>
        <div className="crm-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center">
              <FolderKanban className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-805">{vendorCases.length}</p>
              <p className="text-xs text-slate-500">Active Cases</p>
            </div>
          </div>
        </div>
        <div className="crm-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-805">156</p>
              <p className="text-xs text-slate-500">Completed This Month</p>
            </div>
          </div>
        </div>
        <div className="crm-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-805">₹45,000</p>
              <p className="text-xs text-slate-500">Pending Payout</p>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1">
          <TabsTrigger value="team" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 gap-2">
            <Users className="w-4 h-4" />
            Team FEs ({fieldExecutives.length})
          </TabsTrigger>
          <TabsTrigger value="cases" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 gap-2">
            <FolderKanban className="w-4 h-4" />
            Assigned Cases ({vendorCases.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="team" className="mt-6">
          <DataTable
            columns={feColumns}
            data={fieldExecutives}
            searchPlaceholder="Search field executives..."
            selectable={false}
          />
        </TabsContent>

        <TabsContent value="cases" className="mt-6">
          <DataTable
            columns={caseColumns}
            data={vendorCases}
            searchPlaceholder="Search cases..."
            selectable={true}
            bulkActions={[
              { key: 'assign', label: 'Assign to FE' },
              { key: 'reassign', label: 'Reassign' },
              { key: 'manual_verification', label: 'Manual Verify', icon: CheckCircle2 },
            ]}
            onBulkAction={handleBulkAction}
          />
        </TabsContent>
      </Tabs>

      {/* Manual Verification Modal */}
      <Dialog open={showVerificationModal} onOpenChange={setShowVerificationModal}>
        <DialogContent className="bg-white text-slate-800 border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Manual Verification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Verification Status</Label>
              <Select 
                value={verificationData.status}
                onValueChange={(v) => setVerificationData(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger className="bg-white border-slate-200 text-slate-805">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-805">
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="needs_review">Needs Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea 
                placeholder="Enter verification remarks..."
                value={verificationData.remarks}
                onChange={(e) => setVerificationData(prev => ({ ...prev, remarks: e.target.value }))}
                className="bg-white border-slate-200 min-h-[100px] text-slate-805"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVerificationModal(false)} className="border-slate-200 hover:bg-slate-50 text-slate-605">
              Cancel
            </Button>
            <Button onClick={() => setShowVerificationModal(false)} disabled={!verificationData.status} className="bg-blue-600 hover:bg-blue-700 text-white">
              Submit Verification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
